import * as React from "react";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../ui-kit/kuika-tooltip/kuika-tooltip";
import type { ICodeEditorProblem } from "./code-editor-problems";
import "./code-editor-shell.scss";

/**
 * The editor's own controls, on the row rather than behind a gear: undo, redo, copy, paste,
 * tidy up, wrap and the font stepper. The right half is state — what the language server
 * says, where the cursor is, and full screen.
 */
export interface ICodeEditorToolbarProps {
  onUndo: () => void;
  onRedo: () => void;
  onCopy: () => void;
  onPasteAtCursor: () => void;
  onFormat: () => void;
  formatCompleted: boolean;
  /**
   * What this language's formatter actually does, for the tidy-up tooltip. Each language has
   * its own formatter and its own rules, and "Tidy up" alone does not say which.
   */
  formatHint?: React.ReactNode;
  isWrapEnabled: boolean;
  onToggleWrap: () => void;
  fontSize: number;
  onFontSizeChange: (fontSize: number) => void;
  /** What the language server currently says; counted by severity for the status widget. */
  problems: ICodeEditorProblem[];
  /**
   * Set when the database refused the last run. It counts as an error here too: a green tick
   * next to a failed run reads as "nothing is wrong", which is not what happened.
   */
  runError?: string | null;
  /** Opens the drawer on its Messages tab, the way a status counter opens a problems panel. */
  onShowProblems?: () => void;
  /** Null until the editor has a cursor. */
  cursor: ICodeEditorCursor | null;
  /**
   * Controls that belong to this editor alone, drawn before the state group: the runtime
   * version and the package list a language action carries, which the SQL editor has no
   * equivalent of.
   */
  trailing?: React.ReactNode;
  /** Only the modal can grow to the whole window, so both are optional. */
  isFullScreen?: boolean;
  onToggleFullScreen?: () => void;
}

export interface ICodeEditorCursor {
  lineNumber: number;
  column: number;
}

const MIN_FONT_SIZE: number = 10;
const MAX_FONT_SIZE: number = 24;
const DEFAULT_FONT_SIZE: number = 12;
const FONT_SIZE_STEP: number = 2;

const ErrorGlyph = (): JSX.Element => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="9" />
    <path d="M15 9l-6 6M9 9l6 6" strokeLinecap="round" />
  </svg>
);

const WarningGlyph = (): JSX.Element => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 4l8.5 15h-17L12 4z" strokeLinejoin="round" />
    <path d="M12 10v4M12 16.5v.5" strokeLinecap="round" />
  </svg>
);

const CheckGlyph = (): JSX.Element => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
    <circle cx="12" cy="12" r="9" />
    <path d="M8 12.4l2.7 2.6L16 9.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * The inspection status, the way an IDE puts it: quiet when there is nothing to say, and a
 * count per severity when there is. Clicking it opens the drawer's Messages tab, which is
 * what a status counter is for — so it counts everything that tab lists, the last run's
 * failure included.
 */
const renderStatus = (props: ICodeEditorToolbarProps): JSX.Element => {
  const analysisErrors: number = props.problems.filter(
    (problem: ICodeEditorProblem): boolean => problem.isError
  ).length;
  const hasRunError: boolean = !!props.runError;
  const errors: number = analysisErrors + (hasRunError ? 1 : 0);
  const warnings: number = props.problems.length - analysisErrors;
  const isClean: boolean = errors === 0 && warnings === 0;
  // Enumerated rather than summed, so a failed run is named and not hidden inside a count.
  const label: string =
    [
      analysisErrors > 0 ? `${analysisErrors} error${analysisErrors > 1 ? "s" : ""}` : "",
      warnings > 0 ? `${warnings} warning${warnings > 1 ? "s" : ""}` : "",
      hasRunError ? "Last run failed" : ""
    ]
      .filter(Boolean)
      .join(", ") || "No problems";

  return (
    <KuikaTooltip content={<div>{label}</div>} position="bottom">
      <button
        type="button"
        className={`kuika__code-editor__status${isClean ? " kuika__code-editor__status--clean" : ""}`}
        aria-label={label}
        onClick={props.onShowProblems}
        data-testid="editorProblemCount"
      >
        {isClean ? (
          <span className="kuika__code-editor__status__count kuika__code-editor__status__count--clean">
            <CheckGlyph />
          </span>
        ) : (
          <>
            {errors > 0 && (
              <span className="kuika__code-editor__status__count kuika__code-editor__status__count--error">
                <ErrorGlyph />
                {errors}
              </span>
            )}
            {warnings > 0 && (
              <span className="kuika__code-editor__status__count kuika__code-editor__status__count--warning">
                <WarningGlyph />
                {warnings}
              </span>
            )}
          </>
        )}
      </button>
    </KuikaTooltip>
  );
};

export const CodeEditorToolbar = (props: ICodeEditorToolbarProps): JSX.Element => {
  const stepFontSize = (delta: number): void => {
    const next: number = Math.min(MAX_FONT_SIZE, Math.max(MIN_FONT_SIZE, props.fontSize + delta));
    if (next === props.fontSize) return;
    props.onFontSizeChange(next);
  };

  return (
    <div className="kuika__code-editor__toolbar">
      <KuikaTooltip content={<div>Undo</div>} position="bottom">
        <KuikaButton type="tertiary" color="neutral" isIcon onClick={props.onUndo} aria-label="Undo">
          <KuikaSvg type="arrows" name="arrow-back-up" width={20} height={20} />
        </KuikaButton>
      </KuikaTooltip>

      <KuikaTooltip content={<div>Redo</div>} position="bottom">
        <KuikaButton type="tertiary" color="neutral" isIcon onClick={props.onRedo} aria-label="Redo">
          <KuikaSvg type="arrows" name="arrow-forward-up" width={20} height={20} />
        </KuikaButton>
      </KuikaTooltip>

      <span className="kuika__code-editor__toolbar__separator" />

      <KuikaTooltip content={<div>Copy</div>} position="bottom">
        <KuikaButton
          type="tertiary"
          color="neutral"
          isIcon
          onClick={props.onCopy}
          aria-label="Copy"
          data-testid="editorSettingsCopy"
        >
          <KuikaSvg name="copy" width={20} height={20} />
        </KuikaButton>
      </KuikaTooltip>

      <KuikaTooltip content={<div>Paste</div>} position="bottom">
        <KuikaButton
          type="tertiary"
          color="neutral"
          isIcon
          onClick={props.onPasteAtCursor}
          aria-label="Paste"
          data-testid="pasteButton"
        >
          <KuikaSvg name="paste" width={20} height={20} />
        </KuikaButton>
      </KuikaTooltip>

      <span className="kuika__code-editor__toolbar__separator" />

      <KuikaTooltip
        content={props.formatHint ?? <div>{props.formatCompleted ? "Tidied up" : "Tidy up"}</div>}
        position="bottom"
      >
        <KuikaButton
          type="tertiary"
          color="neutral"
          isIcon
          onClick={props.onFormat}
          aria-label="Tidy up"
          data-testid="tidyUpButton"
        >
          {props.formatCompleted ? (
            <KuikaSvg name="circle-check" width={20} height={20} color="var(--green-5)" />
          ) : (
            <KuikaSvg name="wand" width={20} height={20} />
          )}
        </KuikaButton>
      </KuikaTooltip>

      {/* A toggle button rather than a switch: wrap is a mode, and a switch on a toolbar row
          reads as a setting you have to read before you can use it. */}
      <KuikaTooltip
        content={<div>{props.isWrapEnabled ? "Turn wrapping off" : "Wrap long lines"}</div>}
        position="bottom"
      >
        <KuikaButton
          type="tertiary"
          color="neutral"
          isIcon
          active={props.isWrapEnabled}
          onClick={props.onToggleWrap}
          aria-label="Wrap long lines"
          aria-pressed={props.isWrapEnabled}
          data-testid="wrapButton"
        >
          <KuikaSvg name="text-wrap-column" width={20} height={20} />
        </KuikaButton>
      </KuikaTooltip>

      <span className="kuika__code-editor__toolbar__separator" />

      <span className="kuika__code-editor__font-stepper" data-testid="fontSizeSetting">
        <button
          type="button"
          onClick={(): void => stepFontSize(-FONT_SIZE_STEP)}
          disabled={props.fontSize <= MIN_FONT_SIZE}
          aria-label="Smaller font"
          data-testid="fontSizeDecrease"
        >
          −
        </button>
        <span data-testid="fontSizeValue">{props.fontSize}</span>
        <button
          type="button"
          onClick={(): void => stepFontSize(FONT_SIZE_STEP)}
          disabled={props.fontSize >= MAX_FONT_SIZE}
          aria-label="Bigger font"
          data-testid="fontSizeIncrease"
        >
          +
        </button>
        <button
          type="button"
          className="kuika__code-editor__font-stepper__reset"
          onClick={(): void => props.onFontSizeChange(DEFAULT_FONT_SIZE)}
          disabled={props.fontSize === DEFAULT_FONT_SIZE}
          aria-label="Reset font size"
          data-testid="fontSizeReset"
        >
          <KuikaSvg name="restore" width={14} height={14} />
        </button>
      </span>

      <span className="kuika__code-editor__toolbar__spacer" />

      {props.trailing}

      {renderStatus(props)}

      {props.cursor && (
        <span className="kuika__code-editor__chip" data-testid="editorCursorPosition">
          Ln {props.cursor.lineNumber}, Col {props.cursor.column}
        </span>
      )}

      {props.onToggleFullScreen && (
        <KuikaTooltip content={<div>{props.isFullScreen ? "Exit full screen" : "Full screen"}</div>} position="bottom">
          <KuikaButton
            type="tertiary"
            color="neutral"
            isIcon
            active={props.isFullScreen}
            onClick={props.onToggleFullScreen}
            aria-label={props.isFullScreen ? "Exit full screen" : "Full screen"}
            data-testid="fullScreenButton"
          >
            <KuikaSvg name={props.isFullScreen ? "minimize" : "maximize"} width={20} height={20} />
          </KuikaButton>
        </KuikaTooltip>
      )}
    </div>
  );
};
